import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { MessageCircle, Gauge } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { waLink } from "@/lib/brand";

const SCORE = 72;
const METRICS = [
  { label: "Preparazione", value: 80 },
  { label: "Documentazione", value: 60 },
  { label: "Presentazione", value: 45 },
  { label: "Richiesta della zona", value: 85 },
  { label: "Strategia commerciale", value: null, note: "da definire" },
];

function ScoreRing({ inView }) {
  const [n, setN] = useState(0);
  const R = 96;
  const C = 2 * Math.PI * R;

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const dur = 1600;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(eased * SCORE));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView]);

  return (
    <div className="relative w-[240px] h-[240px]">
      <svg viewBox="0 0 220 220" className="w-full h-full -rotate-90">
        <circle cx="110" cy="110" r={R} fill="none" stroke="#E5E2DA" strokeWidth="14" />
        <motion.circle
          cx="110" cy="110" r={R} fill="none" stroke="#0F5C4A" strokeWidth="14" strokeLinecap="round"
          strokeDasharray={C}
          initial={{ strokeDashoffset: C }}
          animate={inView ? { strokeDashoffset: C - (C * SCORE) / 100 } : {}}
          transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-brand">Casamore Score</span>
        <span className="font-display font-semibold text-anthracite text-6xl leading-none mt-1">{n}</span>
        <span className="text-sm text-anthracite/50 mt-1">su 100</span>
      </div>
    </div>
  );
}

export default function CasamoreScore({ onCalculate }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="score" ref={ref} className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <div className="max-w-2xl">
          <Reveal>
            <Overline>Pronto per la vendita</Overline>
            <h2 className="font-display font-semibold text-anthracite text-5xl sm:text-6xl leading-[0.95] tracking-tight mt-5">
              Quanto è pronto il tuo immobile per essere venduto?
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-anthracite/70">
              Il prezzo è soltanto una parte della vendita. Scopri i punti di forza della tua casa,
              cosa potrebbe rallentarla e come presentarla al mercato.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Ring */}
          <Reveal className="lg:col-span-5 flex justify-center lg:justify-start" delay={0.1}>
            <div className="rounded-3xl bg-white border border-border-subtle p-10 shadow-[0_25px_60px_-30px_rgba(15,92,74,0.3)]">
              <ScoreRing inView={inView} />
            </div>
          </Reveal>

          {/* Metrics */}
          <Reveal className="lg:col-span-7 space-y-6" delay={0.2}>
            {METRICS.map((m, i) => (
              <div key={m.label} data-testid={`metric-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-anthracite">{m.label}</span>
                  <span className={`text-sm font-semibold ${m.value === null ? "text-anthracite/40 italic" : "text-brand"}`}>
                    {m.value === null ? m.note : `${m.value}%`}
                  </span>
                </div>
                <div className="h-2.5 rounded-full bg-accent-subtle overflow-hidden">
                  {m.value === null ? (
                    <div className="h-full w-full bg-[repeating-linear-gradient(45deg,#E5E2DA,#E5E2DA_6px,#F2EFE8_6px,#F2EFE8_12px)]" />
                  ) : (
                    <motion.div
                      className="h-full rounded-full bg-brand"
                      initial={{ width: 0 }}
                      animate={inView ? { width: `${m.value}%` } : {}}
                      transition={{ duration: 1.2, delay: 0.3 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                    />
                  )}
                </div>
              </div>
            ))}

            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <button
                onClick={onCalculate}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white font-semibold px-7 py-4 transition-all hover:bg-brand-hover hover:scale-[1.03]"
                data-testid="score-calculate-cta"
              >
                <Gauge className="h-4 w-4" />
                Calcola il Casamore Score
              </button>
              <a
                href={waLink("Salve Pierluigi, vorrei richiedere un sopralluogo per il mio immobile.")}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-brand text-brand font-semibold px-7 py-4 transition-all hover:bg-brand hover:text-white"
                data-testid="score-visit-cta"
              >
                <MessageCircle className="h-4 w-4" />
                Richiedi un sopralluogo
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <p className="mt-20 font-display italic text-3xl sm:text-4xl leading-tight text-anthracite max-w-3xl">
            Una casa preparata bene non viene semplicemente pubblicata.
            <span className="text-brand"> Viene posizionata per essere scelta.</span>
          </p>
        </Reveal>
      </div>
    </section>
  );
}
